import { getCustomerJwt, CustomerNotLoggedInError } from "./customer";

const INVALID_INPUT_FACE_VALUE_ERR_TYPE = "invalid_input_face_value";

export class InvalidInputFaceValueError extends Error {
  constructor(
    message = "Input value is not within product's accepted price range"
  ) {
    super(message);
    this.name = "InvalidInputFaceValue";
  }
}

/**
 * Adds product to given cart using the internal AddToCart API
 * @param {*} productId
 * @param {*} cartId
 * @param {*} faceValue
 * @returns
 */
export async function addProductToCartViaApi(
  productId,
  cartId,
  faceValue,
  variantId
) {
  const { storeHash, channelId } = window;

  let customerJwt = null;
  let guestCheckoutApiKey = null;
  try {
    customerJwt = await getCustomerJwt(ENV_BC_ACCOUNT_CLIENT_ID);
  } catch (ex) {
    if (ex instanceof CustomerNotLoggedInError) {
      if (window.guestCheckoutAllowed) {
        guestCheckoutApiKey = ENV_SHOP_GUEST_CHECKOUT_API_KEY;
      } else {
        throw new Error("User is not logged in");
      }
    }
  }
  const headers = { "X-Store-Id": storeHash, "X-Channel-Id": channelId };
  if (customerJwt) {
    headers["Authorization"] = `Bearer ${customerJwt}`;
  } else if (guestCheckoutApiKey) {
    headers["X-Api-Key"] = guestCheckoutApiKey;
  }
  const rawResponse = await fetch(
    `${ENV_SHOP_MIDDLEWARE_API_URL}/shop/internal/add-to-cart`,
    {
      method: "POST",
      headers,
      body: JSON.stringify({
        channel_id: channelId,
        cart_id: cartId,
        product_id: productId,
        variant_id: variantId,
        face_value: faceValue,
      }),
    }
  );

  const response = await rawResponse.json();
  if (!rawResponse.ok) {
    const isErrTypeInvalidInputFaceValue =
      response.type === INVALID_INPUT_FACE_VALUE_ERR_TYPE;
    console.error(response);
    if (isErrTypeInvalidInputFaceValue) {
      const invalidInputFaceValueErrMsg = response.message;
      throw new InvalidInputFaceValueError(invalidInputFaceValueErrMsg);
    } else {
      throw new Error(`Failed to add product to cart.`);
    }
  }
  return response;
}

/**
 * Adds product to given cart using the internal AddToCart API
 * @param {*} productId
 * @param {*} cartId
 * @param {*} faceValue
 * @returns
 */
export async function getCart() {
  const response = await fetch("/api/storefront/carts", {
    method: "GET",
    credentials: "include", // Ensures session cookie is sent
  });

  if (!response.ok) {
    throw new Error(`HTTP error! Status: ${response.status}`);
  }

  const carts = await response.json();

  if (carts.length === 0) {
    return;
  }
  return carts[0];
}

export async function deleteCartItem(cartId, itemId) {
  const rawResponse = await fetch(
    `/api/storefront/carts/${cartId}/items/${itemId}`,
    {
      method: "DELETE",
      credentials: "include", // Ensures session cookie is sent
    }
  );
  if (!rawResponse.ok) {
    const response = await rawResponse.json();
    throw new Error(`HTTP error! Status: ${response.status}`);
  }
}

/**
 * Remove temp items on client side for creating cart linked to user session
 */
export async function removeInvalidItemsFromCart() {
  const cart = await getCart();
  for (const item of cart.lineItems.digitalItems) {
    if (item.isCustomPrice === false) {
      await deleteCartItem(cart.id, item.id);
    }
  }
}
